import React, { useRef, useState, useEffect } from "react";
import { Stage, Layer, Line, Group } from "react-konva";

export default function CanvasStage({ width = 800, height = 600, brush, layers = [], onAddStroke, onUpdateLayer }) {
  const stageRef = useRef();
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState([]);

  useEffect(() => {
    // resize or other setup if needed
  }, []);

  const handlePointerDown = (e) => {
    const pos = stageRef.current.getPointerPosition();
    setCurrentPoints([pos.x, pos.y]);
    setIsDrawing(true);
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;
    const pos = stageRef.current.getPointerPosition();
    setCurrentPoints((prev) => {
      const lastX = prev[prev.length - 2];
      const lastY = prev[prev.length - 1];
      if (lastX === pos.x && lastY === pos.y) return prev;
      return [...prev, pos.x, pos.y];
    });
  };

  const handlePointerUp = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentPoints.length > 4) {
      const id = "s-" + Date.now();
      const stroke = {
        id,
        points: currentPoints,
        color: brush.color,
        width: brush.size,
        opacity: brush.opacity,
        compositeOperation: brush.mode === "eraser" ? "destination-out" : "source-over",
        tension: 0.45,
      };
      // Pass stroke to parent; parent will (optionally) create a new layer for this stroke
      onAddStroke(stroke);
    }
    setCurrentPoints([]);
  };

  const renderLayerClip = (layer) => {
    if (!layer.mask) return undefined;
    // mask.type === 'strokes' => mask.sourceLayerId references a layer whose strokes form the mask.
    if (layer.mask.type === "strokes" && layer.mask.sourceLayerId) {
      return (ctx) => {
        const src = layers.find((l) => l.id === layer.mask.sourceLayerId);
        if (!src) return;
        // draw each stroke path as outline using stroke width to create mask region
        ctx.beginPath();
        for (const s of src.strokes) {
          const pts = s.points || [];
          if (pts.length < 4) continue;
          ctx.moveTo(pts[0], pts[1]);
          for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
          // stroke width accounted via drawing to path then stroking with lineWidth, then using globalComposite?
          // We'll approximate by creating subpath then clipping to the stroked path using ctx.lineWidth
        }
        // Note: canvas doesn't allow stroking to affect clip directly; we approximate by clipping to polygonal hull if needed.
        ctx.closePath();
        ctx.clip();
      };
    }
    if (layer.mask.points && layer.mask.points.length >= 6) {
      const pts = layer.mask.points;
      return (ctx) => {
        ctx.beginPath();
        ctx.moveTo(pts[0], pts[1]);
        for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
        ctx.closePath();
        ctx.clip();
      };
    }
    return undefined;
  };

  return (
    <div className="bg-white shadow-lg inline-block">
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {layers.map((layer) => (
          <Layer key={layer.id} visible={layer.visible} clipFunc={renderLayerClip(layer)}>
            <Group>
              {layer.strokes.map((stroke) => (
                <Line
                  key={stroke.id}
                  points={stroke.points}
                  stroke={stroke.color}
                  strokeWidth={stroke.width}
                  tension={stroke.tension}
                  lineCap="round"
                  lineJoin="round"
                  globalCompositeOperation={stroke.compositeOperation}
                  opacity={stroke.opacity}
                />
              ))}
            </Group>
          </Layer>
        ))}

        {isDrawing && currentPoints.length > 0 && (
          <Layer>
            <Line
              points={currentPoints}
              stroke={brush.color}
              strokeWidth={brush.size}
              tension={0.45}
              lineCap="round"
              lineJoin="round"
              opacity={brush.opacity}
            />
          </Layer>
        )}
      </Stage>
    </div>
  );
}

